/**
 * Which account the console is acting on behalf of.
 *
 * Kept in localStorage rather than React state because the api client is not
 * a component and has to attach the header to every request — the same reason
 * the auth token lives there. React reads it through a subscription so the
 * switcher stays in step with whatever set it.
 *
 * WHAT IT ACTUALLY SCOPES
 * Media is scoped, for real: a media job created under one account is absent
 * from another's list and unreachable by its id. Everything else — orders,
 * products, affiliates, posts — is not, and switching accounts does not change
 * what those sections show. The switcher says so rather than implying a
 * separation that only exists in one place so far.
 */

const KEY = 'icepop_account_id';
const CHANGED = 'icepop:account-changed';

export function getSelectedAccountId(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    // Private browsing, blocked storage, or a context without localStorage.
    // A missing selection is handled everywhere; a thrown one is not.
    return null;
  }
}

export function setSelectedAccountId(id: string): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    // Nothing useful to do. The header simply will not be sent, and the
    // backend refuses the request rather than guessing — which is the
    // behaviour we want when we cannot remember the choice.
  }
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: id }));
}

export function clearSelectedAccount(): void {
  try {
    localStorage.removeItem(KEY);
  } catch { /* see above */ }
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: null }));
}

export function onAccountChanged(handler: (id: string | null) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent).detail ?? null);
  window.addEventListener(CHANGED, listener);
  return () => window.removeEventListener(CHANGED, listener);
}
